import type { ReactNode } from 'react';
import type { BreadcrumbItem, UserInfo, AppShellLabels } from './types';
import { Breadcrumb } from './Breadcrumb';
import { UserDropdown } from './UserDropdown';
import { Icon } from './Icon';

interface HeaderBarProps {
  onToggle: () => void;
  breadcrumb: BreadcrumbItem[];
  onBreadcrumbNavigate?: (path: string) => void;
  user: UserInfo;
  onLogout: () => void;
  onProfileClick: () => void;
  onPasswordChange: () => void;
  onActivityHistory?: () => void;
  lang: 'ko' | 'en';
  onLangChange: (lang: 'ko' | 'en') => void;
  /** R3-001 fix: 다크모드 토글 슬롯 (apps/web 의 ThemeToggle 컴포넌트 주입) */
  themeToggle?: ReactNode;
  /** #659: 헤더 우측 액션 슬롯(전역 검색 버튼 등) — apps/web 에서 주입, ui-kit 은 내용 무지 */
  headerActions?: ReactNode;
  /** R6-002: i18n 라벨 */
  labels?: AppShellLabels;
}

export function HeaderBar({
  onToggle,
  breadcrumb,
  onBreadcrumbNavigate,
  user,
  onLogout,
  onProfileClick,
  onPasswordChange,
  onActivityHistory,
  lang,
  onLangChange,
  themeToggle,
  headerActions,
  labels,
}: HeaderBarProps) {
  const menuToggleLabel = labels?.menuToggleAriaLabel ?? '사이드 메뉴 토글';
  const langSwitchLabel = labels?.langSwitchAriaLabel
    ? labels.langSwitchAriaLabel(lang)
    : `언어 전환: 현재 ${lang === 'ko' ? '한국어' : 'English'}`;
  const notificationsLabel = labels?.notificationsAriaLabel ?? '알림 (준비중)';
  return (
    <header
      role="banner"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: 'var(--layout-header-height)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-3)',
        padding: '0 var(--spacing-4)',
        background: 'var(--color-header-bg)',
        borderBottom: '1px solid var(--color-header-border)',
        zIndex: 200,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {/* 햄버거 버튼 */}
      <button
        type="button"
        aria-label={menuToggleLabel}
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          borderRadius: 'var(--radius-md)',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          color: 'var(--color-text-secondary)',
          flexShrink: 0,
          transition: 'background 0.15s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            'var(--color-surface-muted)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'none';
        }}
      >
        <Icon id="menu" size={20} />
      </button>

      {/* 브레드크럼 */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        <Breadcrumb items={breadcrumb} onNavigate={onBreadcrumbNavigate} ariaLabel={labels?.breadcrumbAriaLabel} />
      </div>

      {/* 우측 액션 영역 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          flexShrink: 0,
        }}
      >
        {/* #659: 전역 검색 버튼 등 액션 슬롯 — 테마 토글보다 앞에 노출 */}
        {headerActions}

        {/* R3-001: 다크/라이트 모드 토글 — 모든 인증 페이지 일관 노출 */}
        {themeToggle}

        {/* 언어 스위처 — R7-013: aria-pressed 로 현재 활성 언어 표시 */}
        <button
          type="button"
          aria-label={langSwitchLabel}
          aria-pressed={lang === 'en'}
          onClick={() => onLangChange(lang === 'ko' ? 'en' : 'ko')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            background: 'none',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-sm)',
            padding: '4px var(--spacing-2)',
            cursor: 'pointer',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            transition: 'background 0.15s',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background =
              'var(--color-surface-muted)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'none';
          }}
        >
          <Icon id="globe" size={14} />
          {lang === 'ko' ? 'KO' : 'EN'}
        </button>

        {/* 알림 벨 (placeholder) — hideNotifications=true 이면 미노출 */}
        {!labels?.hideNotifications && (
          <button
            type="button"
            aria-label={notificationsLabel}
            disabled
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: 'none',
              cursor: 'default',
              color: 'var(--color-text-muted)',
              opacity: 0.6,
            }}
          >
            <Icon id="bell" size={18} />
          </button>
        )}

        {/* 사용자 드롭다운 */}
        <UserDropdown
          user={user}
          onLogout={onLogout}
          onProfileClick={onProfileClick}
          onPasswordChange={onPasswordChange}
          onActivityHistory={onActivityHistory}
          userMenuAriaLabel={labels?.userMenuAriaLabel}
          profileLabel={labels?.profileLabel}
          passwordChangeLabel={labels?.passwordChangeLabel}
          activityHistoryLabel={labels?.activityHistoryLabel}
          logoutLabel={labels?.logoutLabel}
          roleLabels={labels?.roleLabels}
        />
      </div>
    </header>
  );
}
